import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { exerciseApi, customExerciseApi } from '@/services/api';
import type { ExerciseLog, CustomExercise } from '@/types';
import { EXERCISES } from '@/types';

interface ExerciseContextType {
  exercises: ExerciseLog[];
  customExercises: CustomExercise[];
  loading: boolean;
  error: string | null;
  createExercise: (data: {
    exercise: string;
    weight: number;
    reps: number;
    sets: number;
    notes?: string;
  }) => Promise<{ success: boolean; isPersonalRecord: boolean }>;
  deleteExercise: (id: string) => Promise<boolean>;
  createCustomExercise: (data: { name: string; category?: string }) => Promise<{ success: boolean; data?: any; message?: string; error?: string }>;
  deleteCustomExercise: (id: string) => Promise<boolean>;
  getAllExerciseNames: () => string[];
  getTodayExercises: () => ExerciseLog[];
  getLastSession: (exerciseName: string) => ExerciseLog | null;
  getRecentlyUsedExercises: () => string[];
  getUniqueUsedExercises: () => Array<{ name: string; lastUsedDate: string; daysAgo: number }>;
  getExerciseProgress: () => Record<string, Array<{ date: string; weight: number; timestamp: string; sets: number; reps: number }>>;
  getRecentPRs: (limit?: number) => ExerciseLog[];
  getExercisesByDate: (date: string) => ExerciseLog[];
  refetch: () => Promise<void>;
}

const ExerciseContext = createContext<ExerciseContextType | undefined>(undefined);

export function ExerciseProvider({ children }: { children: ReactNode }) {
  const [exercises, setExercises] = useState<ExerciseLog[]>([]);
  const [customExercises, setCustomExercises] = useState<CustomExercise[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchExercises = async (params?: { date?: string; exercise?: string }) => {
    setLoading(true);
    setError(null);
    
    const result = await exerciseApi.getExercises(params);
    
    if (result.success) {
      setExercises(result.data || []);
    } else {
      setError(result.error || 'Failed to fetch exercises');
    }
    
    setLoading(false);
  };

  const fetchCustomExercises = async () => {
    const result = await customExerciseApi.getCustomExercises();
    if (result.success) {
      setCustomExercises(result.data || []);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchExercises(), fetchCustomExercises()]);
    setLoading(false);
  };

  const createExercise = async (data: {
    exercise: string;
    weight: number;
    reps: number;
    sets: number;
    notes?: string;
  }) => {
    setLoading(true);
    setError(null);

    const result = await exerciseApi.createExercise(data);
    
    if (result.success) {
      // Refresh exercises after successful creation
      await fetchExercises();
      return { success: true, isPersonalRecord: result.data?.isPersonalRecord || false };
    } else {
      setError(result.error || 'Failed to log exercise');
      setLoading(false);
      return { success: false, isPersonalRecord: false };
    }
  };

  const deleteExercise = async (id: string) => {
    setLoading(true);
    setError(null);

    const result = await exerciseApi.deleteExercise(id);
    
    if (result.success) {
      // Refresh exercises after successful deletion
      await fetchExercises();
      return true;
    } else {
      setError(result.error || 'Failed to delete exercise');
      setLoading(false);
      return false;
    }
  };

  const createCustomExercise = async (data: { name: string; category?: string }) => {
    setLoading(true);
    setError(null);

    const result = await customExerciseApi.createCustomExercise(data);

    if (result.success) {
      // Refresh custom exercises after successful creation
      await fetchCustomExercises();
      setLoading(false);
      return {
        success: true,
        data: result.data,
        message: result.data?.message
      };
    } else {
      setError(result.error || 'Failed to create custom exercise');
      setLoading(false);
      return {
        success: false,
        error: result.error
      };
    }
  };

  const deleteCustomExercise = async (id: string) => {
    setLoading(true);
    setError(null);

    const result = await customExerciseApi.deleteCustomExercise(id);
    
    if (result.success) {
      // Refresh custom exercises after successful deletion
      await fetchCustomExercises();
      return true;
    } else {
      setError(result.error || 'Failed to delete custom exercise');
      setLoading(false);
      return false;
    }
  };

  const getAllExerciseNames = () => {
    const customExerciseNames = customExercises.map(ex => ex.name);
    return [...EXERCISES, ...customExerciseNames];
  };

  const getTodayExercises = () => {
    const today = new Date().toISOString().split('T')[0];
    return exercises.filter(exercise => exercise.date === today);
  };

  const getLastSession = (exerciseName: string) => {
    const exerciseLogs = exercises
      .filter(log => log.exercise === exerciseName)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return exerciseLogs[0] || null;
  };



  const getRecentlyUsedExercises = () => {
    const exerciseCount: Record<string, number> = {};
    exercises.forEach(log => {
      exerciseCount[log.exercise] = (exerciseCount[log.exercise] || 0) + 1;
    });
    
    return Object.entries(exerciseCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 6)
      .map(([exercise]) => exercise);
  };

  const getUniqueUsedExercises = () => {
    const exerciseData: Record<string, Date> = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to start of day for accurate day calculation
    
    // Find the most recent usage date for each exercise
    exercises.forEach(log => {
      const logDate = new Date(log.timestamp);
      if (!exerciseData[log.exercise] || logDate > exerciseData[log.exercise]) {
        exerciseData[log.exercise] = logDate;
      }
    });

    // Calculate days ago for each exercise
    return Object.entries(exerciseData)
      .map(([name, lastUsedDate]) => {
        const lastUsedDateOnly = new Date(lastUsedDate);
        lastUsedDateOnly.setHours(0, 0, 0, 0);
        const diffTime = today.getTime() - lastUsedDateOnly.getTime();
        const daysAgo = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        
        return {
          name,
          lastUsedDate: lastUsedDate.toISOString().split('T')[0],
          daysAgo
        };
      })
      .sort((a, b) => a.daysAgo - b.daysAgo); // Sort by most recently used first
  };

  const getExerciseProgress = () => {
    const exerciseData: Record<string, Array<{ date: string; weight: number; timestamp: string; sets: number; reps: number }>> = {};
    
    exercises.forEach(log => {
      if (!exerciseData[log.exercise]) {
        exerciseData[log.exercise] = [];
      }
      exerciseData[log.exercise].push({
        date: log.date,
        weight: log.weight,
        timestamp: log.timestamp,
        sets: log.sets,
        reps: log.reps
      });
    });

    // Sort by timestamp for each exercise
    Object.keys(exerciseData).forEach(exercise => {
      exerciseData[exercise].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    });

    return exerciseData;
  };

  const getRecentPRs = (limit = 5) => {
    return exercises
      .filter(log => log.isPersonalRecord)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, limit);
  };

  const getExercisesByDate = (date: string) => {
    return exercises
      .filter(log => log.date === date)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  };

  useEffect(() => {
    fetchAll();
  }, []);

  return (
    <ExerciseContext.Provider value={{
      exercises,
      customExercises,
      loading,
      error,
      createExercise,
      deleteExercise,
      createCustomExercise,
      deleteCustomExercise,
      getAllExerciseNames,
      getTodayExercises,
      getLastSession,
      getRecentlyUsedExercises,
      getUniqueUsedExercises,
      getExerciseProgress,
      getRecentPRs,
      getExercisesByDate,
      refetch: fetchAll,
    }}>
      {children}
    </ExerciseContext.Provider>
  );
}

export function useExercises() {
  const context = useContext(ExerciseContext);
  if (context === undefined) {
    throw new Error('useExercises must be used within an ExerciseProvider');
  }
  return context;
}